"use client";

import { useQuery } from "@tanstack/react-query";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { DEFAULT_BLOG_SORT, isSortValue, type QueryBlogsResult, type SortValue } from "./types";
import { BlogOverviewFilters } from "./filters";
import { BlogOverviewResults } from "./results";
import { scrollToElement } from "@/lib/scroll";
import type { CardVariant } from "@/components/frontend/ui/card";
import type { ButtonVariant } from "@/components/frontend/ui/button";
import type { BadgeVariant } from "@/components/frontend/ui/badge/badge";
import type { BlogRoutes } from "@/lib/types/blog";

type SortOption = {
	value: SortValue;
	label: string;
};

type BlogOverviewClientProps = {
	locale: string;
	basePath: string;
	perPage: number;
	columns: string;
	blogCardVariant?: CardVariant;
	badgeVariant?: BadgeVariant;
	paginationButtonVariant?: ButtonVariant;
	blogRoutes?: BlogRoutes;
	initialResult: QueryBlogsResult;
	overviewId?: string | null;
	blogTypeId?: number;
	showSearchBar: boolean;
	showSort: boolean;
	searchPlaceholder: string;
	formVariantId: string;
	sortOptions: SortOption[];
};

function getPage(value: string | null): number {
	const page = parseInt(value ?? "1", 10);
	return Number.isFinite(page) && page > 0 ? page : 1;
}

export function BlogOverviewClient({
	locale,
	basePath,
	perPage,
	columns,
	blogRoutes,
	initialResult,
	overviewId,
	blogTypeId,
	showSearchBar,
	showSort,
	searchPlaceholder,
	formVariantId,
	sortOptions,
	blogCardVariant,
	badgeVariant,
	paginationButtonVariant,
}: BlogOverviewClientProps) {
	const t = useTranslations("blog");
	const router = useRouter();
	const searchParams = useSearchParams();
	const page = getPage(searchParams.get("page"));
	const query = searchParams.get("q") ?? "";
	const sortParam = searchParams.get("sort") ?? undefined;
	const sort = isSortValue(sortParam) ? sortParam : DEFAULT_BLOG_SORT;
	const isDefaultView = page === 1 && sort === DEFAULT_BLOG_SORT && query.trim().length === 0;

	const { data, isFetching } = useQuery({
		queryKey: ["blogs", page, perPage, sort, query, blogTypeId],
		queryFn: async () => {
			const params = new URLSearchParams({
				page: page.toString(),
				perPage: perPage.toString(),
				sort,
			});
			if (query.trim().length > 0) params.set("q", query.trim());
			if (blogTypeId !== undefined) params.set("blogType", blogTypeId.toString());

			const response = await fetch(`/${locale}/next/blog-overview?${params.toString()}`);
			if (!response.ok) throw new Error("Failed to load blogs.");

			return response.json() as Promise<QueryBlogsResult>;
		},
		initialData: isDefaultView ? initialResult : undefined,
		placeholderData: (previousData) => previousData ?? initialResult,
	});

	const updateParams = (nextPage: number, nextQuery = query, nextSort = sort) => {
		const params = new URLSearchParams(searchParams.toString());
		params.delete("page");
		params.delete("q");
		params.delete("sort");
		if (nextQuery.trim().length > 0) params.set("q", nextQuery.trim());
		if (nextSort !== DEFAULT_BLOG_SORT) params.set("sort", nextSort);
		if (nextPage > 1) params.set("page", nextPage.toString());

		const next = params.toString();
		router.push(next ? `${basePath}?${next}` : basePath, { scroll: false });
		if (overviewId && nextPage !== page) scrollToElement(`#${overviewId}`);
	};

	const result = data ?? initialResult;
	const resultsSummary = t("results-summary", {
		from: result.totalDocs > 0 ? (result.page - 1) * perPage + 1 : 0,
		to: result.totalDocs > 0 ? (result.page - 1) * perPage + result.docs.length : 0,
		total: result.totalDocs,
	});

	return (
		<>
			<BlogOverviewFilters
				basePath={basePath}
				showSearchBar={showSearchBar}
				showSort={showSort}
				initialQuery={query}
				initialSort={sort}
				searchPlaceholder={searchPlaceholder}
				formVariantId={formVariantId}
				sortOptions={sortOptions}
			/>

			<BlogOverviewResults
				basePath={basePath}
				columns={columns}
				blogRoutes={blogRoutes}
				result={result}
				query={query}
				sort={sort}
				blogCardVariant={blogCardVariant}
				badgeVariant={badgeVariant}
				paginationButtonVariant={paginationButtonVariant}
				pending={isFetching}
				onPageChange={(nextPage) => updateParams(nextPage)}
				resultsSummary={resultsSummary}
				noResultsTitle={t("no-results-title")}
				noResultsDescription={t("no-results-description")}
			/>
		</>
	);
}
